import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Search, X, Filter, Wifi, Ticket, TicketX, ExternalLink, Users, DownloadCloud, Sparkles, ChevronUp, ChevronDown, ChevronsUpDown, Columns3, UserPlus } from "lucide-react";
import { ClientDrawer } from "@/pages/admin/MigrationBoard";
import SyncroImportModal from "@/components/SyncroImportModal";
import ManualAddCustomerModal from "@/components/ManualAddCustomerModal";

// Stage list must mirror the Kanban DEFAULT_STAGES in the backend
// (``commissions/migrations.py``). Colors matched exactly so the
// customer-list badge reads the same colour as the Kanban column
// header for a given stage — one visual language across surfaces.
const STAGES = [
  { key: "not_contacted",  label: "Not contacted",   color: "#64748b" },
  { key: "contacted",      label: "Contacted",       color: "#0ea5e9" },
  { key: "info_sent",      label: "Info sent",       color: "#6366f1" },
  { key: "form_submitted", label: "Form submitted",  color: "#a855f7" },
  { key: "provisioning",   label: "Provisioning",    color: "#f59e0b" },
  { key: "installed",      label: "Installed",       color: "#eab308" },
  { key: "live",           label: "Live",            color: "#10b981" },
  { key: "completed",      label: "Completed",       color: "#059669" },
  { key: "lost",           label: "Lost or declined", color: "#ef4444" },
  // Legacy keys kept so any old customer rows still render a coloured
  // badge instead of falling through to the slate fallback.
  { key: "requested",   label: "Requested",   color: "#64748b", legacy: true },
  { key: "in_progress", label: "In progress", color: "#0ea5e9", legacy: true },
  { key: "handover",    label: "Handover",    color: "#a855f7", legacy: true },
  { key: "porting",     label: "Porting",     color: "#f59e0b", legacy: true },
  { key: "cancelled",   label: "Cancelled",   color: "#ef4444", legacy: true },
];

const STAGE_BY_KEY = Object.fromEntries(STAGES.map((s) => [s.key, s]));

// Available toggle-able columns on the customers table. Business name
// is always shown (primary key of the row), so it's not in this list.
// ``defaultOn`` picks the out-of-the-box set for new admins; users
// can override via the "Columns" dropdown and the selection persists
// per-browser in localStorage. ``adminOnly`` hides the row from the
// dropdown entirely for partner-portal users so their toggle list
// stays scoped to what they can actually see.
const COLUMN_DEFS = [
  { key: "contact",         label: "Contact",      defaultOn: true  },
  { key: "suburb",          label: "Suburb",       defaultOn: true  },
  { key: "address",         label: "Full address", defaultOn: false },
  { key: "avc",             label: "AVC",          defaultOn: true  },
  { key: "nbn_username",    label: "NBN username", defaultOn: true  },
  { key: "nbn_location_id", label: "NBN Loc ID",   defaultOn: false },
  { key: "created",         label: "Date added",   defaultOn: false },
  { key: "stage",           label: "Stage",        defaultOn: true  },
  { key: "partner",         label: "Partner",      defaultOn: true, adminOnly: true },
  { key: "ticket",          label: "Ticket",       defaultOn: true  },
];
const COLS_STORAGE_KEY = "customers.visibleCols";

// Sortable-column config for the customers table. ``accessor`` returns
// the value used for comparison — cast to lowercase for strings so
// case doesn't matter, and to number/timestamp where it makes more
// sense than a lexical sort (Stage sorts by the stage's `order`
// field on the STAGES list so "Not contacted" comes before "Live").
// Cycle: unsorted → asc → desc → back to default (business_name asc).
const SORT_COLUMNS = {
  business_name: {
    accessor: (r) => (r.business_name || r.contact_name || r.email || "").toLowerCase(),
  },
  contact_name: {
    accessor: (r) => (r.contact_name || "").toLowerCase(),
  },
  suburb: {
    accessor: (r) => suburbFor(r).toLowerCase(),
  },
  avc_number: {
    accessor: (r) => (r.avc_number || "").toLowerCase(),
  },
  nbn_username: {
    accessor: (r) => (r.nbn_username || "").toLowerCase(),
  },
  // Date added — sorts by created_at timestamp. Missing timestamps
  // (older Syncro-backfilled rows) fall to the bottom on asc sort.
  created_at: {
    accessor: (r) => r.created_at ? new Date(r.created_at).getTime() : 0,
  },
  stage: {
    accessor: (r) => {
      // Sort by the stage's canonical order so the visual sort
      // matches the Kanban left-to-right progression.
      const idx = STAGES.findIndex((s) => s.key === r.stage);
      return idx === -1 ? 999 : idx;
    },
  },
  partner_code: {
    accessor: (r) => (r.partner_code || "").toLowerCase(),
  },
  syncro_ticket_number: {
    // Numeric where possible so #61351 sorts after #9999, not before.
    accessor: (r) => {
      const n = Number(r.syncro_ticket_number);
      return Number.isFinite(n) ? n : (r.syncro_ticket_number || "").toString().toLowerCase();
    },
  },
};

/**
 * Shared customers list. Backend already scopes the /commissions/customers
 * endpoint by role (partner → own only, admin → all), so this component
 * doesn't need its own role logic beyond deciding which columns to show.
 */
export default function CustomersList({ role }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [stage, setStage] = useState(searchParams.get("stage") || "");
  const [openId, setOpenId] = useState(searchParams.get("open") || null);
  // Admin-only URL filter: ``?partner=<code>`` narrows the list to a single
  // partner's customers. Populated by the "View all customers" deep-link on
  // ``/admin/partners/<code>``. Partners never see this — their scoping is
  // server-side.
  const partnerFilter = role === "admin" ? (searchParams.get("partner") || "").toLowerCase() : "";
  const [showImport, setShowImport] = useState(false);
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [backfilling, setBackfilling] = useState(false);
  // Client-side chip filter — hides rows that already have a Syncro ticket
  // so admins can clear the "missing ticket" backlog in one focused view.
  const [showMissingTicket, setShowMissingTicket] = useState(
    searchParams.get("missing_ticket") === "1"
  );
  // Sort state on the table headers — first click asc, second desc,
  // third click clears back to default (business_name asc).
  const [sort, setSort] = useState({
    key: searchParams.get("sort_key") || "business_name",
    dir: (searchParams.get("sort_dir") === "desc" ? "desc" : "asc"),
  });
  // Column visibility — persisted to localStorage per-browser so an
  // admin's chosen columns survive reload. Defaults come from the
  // ``defaultOn`` flag on COLUMN_DEFS. Partners never see the
  // "Partner" toggle regardless of what's in localStorage.
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const raw = localStorage.getItem(COLS_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch { /* localStorage disabled / JSON parse failure — fall through */ }
    return COLUMN_DEFS.filter((c) => c.defaultOn).map((c) => c.key);
  });
  useEffect(() => {
    try { localStorage.setItem(COLS_STORAGE_KEY, JSON.stringify(visibleCols)); }
    catch { /* localStorage disabled — silent no-op */ }
  }, [visibleCols]);
  const toggleCol = (k) => setVisibleCols((v) =>
    v.includes(k) ? v.filter((x) => x !== k) : [...v, k]
  );
  const showCol = (k) => visibleCols.includes(k);
  function cycleSort(key) {
    setSort((cur) => {
      if (cur.key !== key) return { key, dir: "asc" };
      if (cur.dir === "asc") return { key, dir: "desc" };
      return { key: "business_name", dir: "asc" };
    });
  }

  async function runBackfill() {
    if (backfilling) return;
    if (!window.confirm(
      "Sweep commission history and create Customer records for any invoiced customer "
      + "who isn't already on the Kanban? Safe to run multiple times — existing customers are skipped."
    )) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/commissions/customers/backfill-from-events");
      const msgs = [];
      msgs.push(`${data.created} added`);
      if (data.linked_syncro) msgs.push(`${data.linked_syncro} auto-linked to Syncro`);
      if (data.skipped_existing) msgs.push(`${data.skipped_existing} already existed`);
      toast.success(`Backfill complete — ${msgs.join(", ")}`);
      setReloadTick((n) => n + 1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  }

  // Debounced load — 250 ms after the last keystroke.
  useEffect(() => {
    const h = setTimeout(async () => {
      try {
        const params = {};
        if (q) params.q = q;
        if (stage) params.stage = stage;
        if (partnerFilter) params.partner_code = partnerFilter;
        const { data } = await api.get("/commissions/customers", { params });
        setRows(data.customers || []);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load customers");
      }
    }, 250);
    return () => clearTimeout(h);
  }, [q, stage, partnerFilter, reloadTick]);

  // Keep the URL in sync so a shared link or a refresh preserves the
  // search + selection state (also lets HeaderSearch deep-link straight
  // into a specific customer).
  useEffect(() => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (stage) next.set("stage", stage);
    if (openId) next.set("open", openId);
    if (showMissingTicket) next.set("missing_ticket", "1");
    if (partnerFilter) next.set("partner", partnerFilter);
    if (sort.key !== "business_name" || sort.dir !== "asc") {
      next.set("sort_key", sort.key);
      next.set("sort_dir", sort.dir);
    }
    setSearchParams(next, { replace: true });
  }, [q, stage, openId, showMissingTicket, partnerFilter, sort, setSearchParams]);

  const visibleRows = useMemo(() => {
    if (!rows) return null;
    let out = rows;
    if (showMissingTicket) out = out.filter((r) => !r.syncro_ticket_number);
    // Apply sort if we have a matching column config
    const col = SORT_COLUMNS[sort.key];
    if (col) {
      const dir = sort.dir === "asc" ? 1 : -1;
      out = [...out].sort((a, b) => {
        const av = col.accessor(a);
        const bv = col.accessor(b);
        if (av < bv) return -1 * dir;
        if (av > bv) return  1 * dir;
        return 0;
      });
    }
    return out;
  }, [rows, showMissingTicket, sort]);

  const total = visibleRows?.length ?? 0;
  const missingTicketCount = useMemo(
    () => (rows || []).filter((r) => !r.syncro_ticket_number).length,
    [rows]
  );
  const stageCounts = useMemo(() => {
    const m = new Map();
    (rows || []).forEach((r) => m.set(r.stage, (m.get(r.stage) || 0) + 1));
    return m;
  }, [rows]);

  return (
    <div className="space-y-8" data-testid={`${role}-customers-page`}>
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl tracking-tight font-medium">Customers</h1>
          <p className="text-slate-400 mt-1.5 text-sm max-w-2xl">
            {role === "admin"
              ? "Every customer across every partner — search NBN usernames, addresses, or Syncro ticket numbers to jump straight into the record."
              : "Your customers. Search by name, address, or NBN username and open the record for credentials and ticket links."}
          </p>
        </div>
        <div className="inline-flex items-center gap-2">
          {role === "admin" && (
            <>
              <button
                type="button"
                onClick={runBackfill}
                disabled={backfilling}
                data-testid="customers-backfill-btn"
                title="Sweep commission history for customers not yet on the Kanban and create bare records for them."
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-wait"
              >
                <Sparkles className="w-4 h-4" />
                {backfilling ? "Backfilling…" : "Backfill from commissions"}
              </button>
              <button
                type="button"
                onClick={() => setShowImport(true)}
                data-testid="customers-import-syncro-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 text-sm font-semibold transition-colors"
              >
                <DownloadCloud className="w-4 h-4" /> Import from Syncro
              </button>
              <button
                type="button"
                onClick={() => setShowManualAdd(true)}
                data-testid="customers-manual-add-btn"
                title="Add a customer that never lived in Syncro"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 text-sm font-semibold transition-colors"
              >
                <UserPlus className="w-4 h-4" /> Add customer
              </button>
            </>
          )}
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-slate-800 bg-slate-900 text-slate-300 text-xs font-medium">
            <Users className="w-3.5 h-3.5" />
            <span data-testid="customers-total-count">{rows === null ? "—" : total}</span> {total === 1 ? "customer" : "customers"}
          </div>
        </div>
      </header>

      {/* Partner filter chip — visible only when the URL has ?partner=<code> */}
      {partnerFilter && (
        <div
          data-testid="customers-partner-filter-chip"
          className="rounded-xl border border-cyan-500/40 bg-cyan-500/5 p-4 flex items-center justify-between gap-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 shrink-0 grid place-items-center rounded-full bg-cyan-500/15 text-cyan-300">
              <Users className="w-4 h-4" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold">
                Filtered by partner
              </div>
              <div className="text-sm font-mono text-cyan-100 mt-0.5">{partnerFilter}</div>
            </div>
          </div>
          <button
            type="button"
            data-testid="customers-partner-filter-clear"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete("partner");
              setSearchParams(next, { replace: true });
            }}
            className="inline-flex items-center gap-1.5 text-xs text-cyan-300 hover:text-cyan-100 border border-cyan-500/40 hover:border-cyan-400 bg-cyan-500/10 hover:bg-cyan-500/20 rounded-full px-3 py-1.5 font-semibold transition-colors"
          >
            <X className="w-3.5 h-3.5" /> Clear filter
          </button>
        </div>
      )}

      {/* Search + filter */}
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="customers-search-input"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search business, contact, email, phone, NBN username, address, ticket #…"
            className="w-full rounded-lg border border-slate-800 bg-slate-950 pl-9 pr-9 py-2.5 text-sm text-slate-50 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-500 hover:text-slate-200"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-slate-500" />
          <select
            data-testid="customers-stage-filter"
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            className="rounded-lg border border-slate-800 bg-slate-950 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-cyan-500"
          >
            <option value="">All stages ({rows?.length ?? 0})</option>
            {STAGES.filter((s) => !s.legacy).map((s) => (
              <option key={s.key} value={s.key}>
                {s.label} ({stageCounts.get(s.key) || 0})
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setShowMissingTicket((v) => !v)}
            data-testid="customers-missing-ticket-toggle"
            title="Show only customers who haven't been pushed to Syncro yet"
            className={`inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg border text-xs font-semibold transition-colors ${
              showMissingTicket
                ? "border-amber-500/60 bg-amber-500/15 text-amber-200"
                : "border-slate-800 bg-slate-950 text-slate-400 hover:text-amber-200 hover:border-amber-500/40"
            }`}
          >
            <TicketX className="w-3.5 h-3.5" />
            Missing Syncro ticket
            {missingTicketCount > 0 && (
              <span className={`inline-flex items-center justify-center min-w-[1.25rem] px-1 rounded-full text-[10px] font-mono ${
                showMissingTicket ? "bg-amber-500/30 text-amber-100" : "bg-slate-800 text-slate-300"
              }`}>
                {missingTicketCount}
              </span>
            )}
          </button>
          <ColumnsMenu role={role} visibleCols={visibleCols} onToggle={toggleCol} />
        </div>
      </div>

      {/* Table */}
      {rows === null ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : visibleRows.length === 0 ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-10 text-center" data-testid="customers-empty">
          <Users className="w-8 h-8 mx-auto text-slate-600 mb-3" />
          <div className="text-slate-300 font-medium">
            {q || stage || showMissingTicket ? "No customers match your filters" : "No customers yet"}
          </div>
          <div className="text-slate-500 text-sm mt-1">
            {q || stage || showMissingTicket
              ? "Try a shorter search or clear the filters above."
              : (role === "admin"
                  ? "Customers appear here after a partner adds them to the Onboarding board or submits the signup wizard."
                  : "New customers appear here after you add them to Onboarding or a signup lands.")}
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <tr>
                  <SortableTh sortKey="business_name" sort={sort} onSort={cycleSort} label="Business" />
                  {showCol("contact") && (
                    <SortableTh sortKey="contact_name"  sort={sort} onSort={cycleSort} label="Contact"  className="hidden md:table-cell" />
                  )}
                  {showCol("suburb") && (
                    <SortableTh sortKey="suburb"        sort={sort} onSort={cycleSort} label="Suburb"   className="hidden lg:table-cell" />
                  )}
                  {showCol("address") && (
                    <th className="text-left px-4 py-3 hidden xl:table-cell text-slate-500 uppercase tracking-wider">Address</th>
                  )}
                  {showCol("avc") && (
                    <SortableTh sortKey="avc_number"    sort={sort} onSort={cycleSort} label="AVC"      className="hidden lg:table-cell" />
                  )}
                  {showCol("nbn_username") && (
                    <SortableTh sortKey="nbn_username"  sort={sort} onSort={cycleSort} label="NBN username" />
                  )}
                  {showCol("nbn_location_id") && (
                    <th className="text-left px-4 py-3 hidden xl:table-cell text-slate-500 uppercase tracking-wider">NBN Loc ID</th>
                  )}
                  {showCol("created") && (
                    <SortableTh sortKey="created_at" sort={sort} onSort={cycleSort} label="Date added" className="hidden xl:table-cell" />
                  )}
                  {showCol("stage") && (
                    <SortableTh sortKey="stage"         sort={sort} onSort={cycleSort} label="Stage"    className="hidden md:table-cell" />
                  )}
                  {role === "admin" && showCol("partner") && (
                    <SortableTh sortKey="partner_code" sort={sort} onSort={cycleSort} label="Partner" className="hidden xl:table-cell" />
                  )}
                  {showCol("ticket") && (
                    <SortableTh sortKey="syncro_ticket_number" sort={sort} onSort={cycleSort} label="Ticket" />
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {visibleRows.map((r) => (
                  <tr
                    key={r.id}
                    data-testid={`customer-row-${r.id}`}
                    onClick={() => setOpenId(r.id)}
                    className="cursor-pointer hover:bg-slate-950/60 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-50 truncate max-w-[240px]" title={r.business_name}>
                        {r.business_name || r.contact_name || "(no name)"}
                      </div>
                      {r.email && <div className="text-xs text-slate-500 truncate max-w-[240px]">{r.email}</div>}
                    </td>
                    {showCol("contact") && (
                      <td className="px-4 py-3 text-slate-300 hidden md:table-cell">{r.contact_name || "—"}</td>
                    )}
                    {showCol("suburb") && (
                      <td className="px-4 py-3 text-slate-300 hidden lg:table-cell" title={r.service_address}>
                        {suburbFor(r) || <span className="text-slate-600">—</span>}
                      </td>
                    )}
                    {showCol("address") && (
                      <td className="px-4 py-3 text-slate-400 text-xs truncate max-w-[280px] hidden xl:table-cell" title={r.service_address}>
                        {r.service_address || <span className="text-slate-600">—</span>}
                      </td>
                    )}
                    {showCol("avc") && (
                      <td className="px-4 py-3 hidden lg:table-cell">
                        {r.avc_number ? (
                          <span className="font-mono text-xs text-cyan-200/90" title={r.avc_number}>{r.avc_number}</span>
                        ) : <span className="text-slate-600">—</span>}
                      </td>
                    )}
                    {showCol("nbn_username") && (
                      <td className="px-4 py-3">
                        {r.nbn_username ? (
                          <span className="inline-flex items-center gap-1.5 font-mono text-xs text-cyan-200">
                            <Wifi className="w-3 h-3" /> {r.nbn_username}
                          </span>
                        ) : <span className="text-slate-600">—</span>}
                      </td>
                    )}
                    {showCol("nbn_location_id") && (
                      <td className="px-4 py-3 hidden xl:table-cell">
                        {r.nbn_location_id ? (
                          <span className="font-mono text-xs text-slate-300" title={r.nbn_location_id}>{r.nbn_location_id}</span>
                        ) : <span className="text-slate-600">—</span>}
                      </td>
                    )}
                    {showCol("created") && (
                      <td className="px-4 py-3 hidden xl:table-cell text-xs text-slate-400 whitespace-nowrap" title={r.created_at || ""}>
                        {r.created_at
                          ? new Date(r.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" })
                          : <span className="text-slate-600">—</span>}
                      </td>
                    )}
                    {showCol("stage") && (
                    <td className="px-4 py-3 hidden md:table-cell">
                      {r.stage ? (() => {
                        const s = STAGE_BY_KEY[r.stage];
                        const color = s?.color || "#64748b";
                        const label = s?.label || r.stage.replace(/_/g, " ");
                        return (
                          <span
                            data-testid={`customer-stage-${r.id}`}
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider"
                            style={{
                              borderColor: `${color}66`,
                              backgroundColor: `${color}1f`,
                              color: color,
                            }}
                          >
                            <span
                              aria-hidden
                              className="w-1.5 h-1.5 rounded-full shrink-0"
                              style={{ background: color }}
                            />
                            {label}
                          </span>
                        );
                      })() : <span className="text-slate-600">—</span>}
                    </td>
                    )}
                    {role === "admin" && showCol("partner") && (
                      <td className="px-4 py-3 hidden xl:table-cell">
                        {r.partner_code ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 text-[10px] font-mono">
                            {r.partner_code}
                          </span>
                        ) : <span className="text-slate-600">—</span>}
                      </td>
                    )}
                    {showCol("ticket") && (
                    <td className="px-4 py-3">
                      {r.syncro_ticket_number ? (
                        r.syncro_ticket_url ? (
                          <a
                            href={r.syncro_ticket_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 text-cyan-300 hover:text-cyan-200 font-mono text-xs"
                          >
                            <Ticket className="w-3 h-3" /> #{r.syncro_ticket_number}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <span className="inline-flex items-center gap-1 font-mono text-xs text-slate-300">
                            <Ticket className="w-3 h-3" /> #{r.syncro_ticket_number}
                          </span>
                        )
                      ) : <span className="text-slate-600">—</span>}
                    </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {openId && (
        <CustomerEditDrawer
          customerId={openId}
          role={role}
          onClose={() => setOpenId(null)}
          onChanged={() => setReloadTick((n) => n + 1)}
        />
      )}

      {showImport && (
        <SyncroImportModal
          onClose={() => setShowImport(false)}
          onImported={() => { setShowImport(false); setReloadTick((n) => n + 1); }}
        />
      )}

      {showManualAdd && (
        <ManualAddCustomerModal
          onClose={() => setShowManualAdd(false)}
          onCreated={(client) => {
            setShowManualAdd(false);
            setReloadTick((n) => n + 1);
            if (client?.id) setOpenId(client.id);
          }}
        />
      )}
    </div>
  );
}

/**
 * Thin wrapper around the Kanban board's ``<ClientDrawer>`` so the
 * Customers list opens the exact same edit UX. Loads the customer
 * record + the customer's partner board (needed for the stage
 * selector inside the drawer), then hands both off.
 *
 * Partners hit ``/partner/migration`` (their own board); admins hit
 * the specific partner's board via ``/commissions/migrations/{code}``.
 */
function CustomerEditDrawer({ customerId, role, onClose, onChanged }) {
  const [client, setClient] = useState(null);
  const [board, setBoard] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setErr(null);
      try {
        // 1) Load the customer (comes back with decrypted NBN).
        const { data: c } = await api.get(`/commissions/customers/${customerId}`);
        if (!alive) return;
        setClient(c);
        // 2) Load the board that owns this customer so the stage
        //    dropdown / drag-target list in the drawer works.
        const boardUrl = role === "partner"
          ? "/partner/migration"
          : `/commissions/migrations/${c.partner_code}`;
        const { data: b } = await api.get(boardUrl);
        if (!alive) return;
        setBoard(b.board || null);
      } catch (e) {
        if (!alive) return;
        setErr(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load customer");
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load customer");
        onClose();
      }
    })();
    return () => { alive = false; };
  }, [customerId, role, onClose]);

  // Placeholder shell so the sliding overlay animates in even while
  // the two requests are inflight — same UX as the Kanban.
  if (!client || !board) {
    return (
      <div
        className="fixed inset-0 z-50 flex"
        data-testid="customer-edit-drawer-loading"
      >
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="flex-1 bg-slate-950/70 backdrop-blur-sm"
        />
        <aside className="w-full max-w-2xl bg-slate-900 border-l border-slate-800 p-8 text-slate-500 text-sm">
          {err || "Loading customer…"}
        </aside>
      </div>
    );
  }

  return (
    <ClientDrawer
      client={client}
      board={board}
      isPartnerView={role === "partner"}
      onClose={onClose}
      onChanged={(next) => {
        // The Kanban's drawer passes back either the updated client,
        // the string "deleted", or "reset". For the customers list we
        // just refresh the underlying list, then close on deletion.
        if (next === "deleted") {
          onChanged();
          onClose();
          return;
        }
        if (next && typeof next === "object") setClient(next);
        onChanged();
      }}
    />
  );
}

function shortSuburb(address) {
  if (!address) return "";
  const parts = address.split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 1) return address;
  return parts.slice(-2).join(", ");
}

// Aussie street-type tokens used to pinpoint where the address body ends
// and the suburb begins in a free-text string like
// ``"579 Wyse St Albury, NSW 2640"`` → suburb "Albury".
const AU_STREET_TYPES = /\b(St|Street|Rd|Road|Ave|Avenue|Cct|Circuit|Cres|Crescent|Dr|Drive|Pl|Place|Ct|Court|Way|Ln|Lane|Tce|Terrace|Pde|Parade|Hwy|Highway|Blvd|Boulevard|Grove|Gr|Cir|Loop|Sq|Square|Prom|Promenade|Row)\b/i;
const AU_STATES = /(NSW|VIC|QLD|WA|SA|TAS|NT|ACT)/i;

// Extract just the suburb from a free-text Aussie address. The customer
// list is meant to be scannable — showing the full ``"NSW, 2640"`` tail
// on every row buries the useful bit. Handles the two common Aussie
// formats:
//   * Formal / comma-separated: ``"12 Main St, GLENROY, NSW, 2640"``
//     → last comma segment before the state = suburb.
//   * Informal / street-type only: ``"579 Wyse St Albury, NSW 2640"``
//     → text after the last street-type token on the pre-state segment.
// Falls back to the last comma segment when neither pattern matches.
function extractSuburb(address) {
  if (!address) return "";
  const m = address.match(AU_STATES);
  if (!m) {
    // No state token — best guess is the last comma segment.
    const parts = address.split(",").map((s) => s.trim()).filter(Boolean);
    return parts[parts.length - 1] || "";
  }
  const before = address.slice(0, m.index).replace(/[\s,]+$/, "");
  const parts = before.split(",").map((s) => s.trim()).filter(Boolean);
  const last = parts[parts.length - 1] || "";
  // If the segment before the state still contains a street-type token
  // (informal address), the suburb is whatever follows it.
  const stMatch = last.match(AU_STREET_TYPES);
  if (stMatch) {
    const after = last.slice(stMatch.index + stMatch[0].length).trim();
    if (after) return after.replace(/[,\s]+$/, "");
  }
  return last;
}

// Row-level suburb picker. Prefer the structured
// ``service_address_components`` when they exist (populated by the
// signup Google-Places flow and by the Syncro hydrate sweep in
// v1.10.37+ — always clean, always just the suburb name). Fall back
// to the regex extraction against the free-text ``service_address``
// for pre-hydration rows.
function suburbFor(row) {
  const comps = row?.service_address_components;
  if (comps && typeof comps === "object" && (comps.suburb || comps.city)) {
    return (comps.suburb || comps.city || "").trim();
  }
  return extractSuburb(row?.service_address);
}

/**
 * Sortable table-header cell. Kept at module level so React doesn't
 * warn about "unstable nested components" — the parent passes its
 * ``sort`` state and ``onSort`` (a.k.a. ``cycleSort``) in as props.
 * Clicking cycles asc → desc → cleared. The active column shows a
 * cyan chevron; inactive columns show a low-opacity double-chevron.
 */
function SortableTh({ sortKey, sort, onSort, label, className = "" }) {
  const active = sort?.key === sortKey;
  const Icon = !active ? ChevronsUpDown : (sort.dir === "asc" ? ChevronUp : ChevronDown);
  return (
    <th className={`text-left px-4 py-3 ${className}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        data-testid={`customers-sort-${sortKey}`}
        aria-label={`Sort by ${label}`}
        className="inline-flex items-center gap-1.5 uppercase tracking-wider hover:text-cyan-300 transition-colors"
      >
        {label}
        <Icon className={`w-3 h-3 ${active ? "text-cyan-300" : "opacity-40"}`} />
      </button>
    </th>
  );
}


/**
 * Popover-style column-visibility toggle. Uses ``<details>``/
 * ``<summary>`` for a11y-friendly open/close without any extra
 * state or click-outside plumbing. Filters out ``adminOnly`` rows
 * for partner viewers so they never see toggles they can't use.
 */
function ColumnsMenu({ role, visibleCols, onToggle }) {
  const items = COLUMN_DEFS.filter((c) => !c.adminOnly || role === "admin");
  return (
    <details className="relative" data-testid="customers-columns-menu">
      <summary
        className="list-none inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-slate-800 bg-slate-950 text-slate-400 hover:text-cyan-200 hover:border-cyan-500/40 cursor-pointer select-none text-xs font-semibold transition-colors"
        title="Show or hide columns"
      >
        <Columns3 className="w-3.5 h-3.5" />
        Columns
      </summary>
      <div className="absolute right-0 mt-1.5 z-20 w-52 rounded-lg border border-slate-800 bg-slate-950 shadow-2xl p-1.5">
        <div className="px-2 pt-1 pb-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
          Visible columns
        </div>
        {items.map((c) => {
          const checked = visibleCols.includes(c.key);
          return (
            <label
              key={c.key}
              className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-900 rounded cursor-pointer text-sm text-slate-200 select-none"
              data-testid={`customers-col-toggle-${c.key}`}
            >
              <input
                type="checkbox"
                className="rounded border-slate-700 bg-slate-900 text-cyan-500 focus:ring-1 focus:ring-cyan-500/40"
                checked={checked}
                onChange={() => onToggle(c.key)}
              />
              <span className={checked ? "" : "text-slate-500"}>{c.label}</span>
            </label>
          );
        })}
      </div>
    </details>
  );
}
